'use client';

import { useEffect, useRef } from 'react';

import Image from 'next/image';
import Link from 'next/link';

import { AnalyticsPreferencesButton } from './marketing-analytics';
import { WebsiteLinkButton } from './website-elements';
import { WebsiteLandscapeVideo } from './website-landscape-video';
import styles from './website-pill-cta.module.css';

export function WebsitePillCta({ id = 'next-step' }: { id?: string }) {
  const section = useRef<HTMLElement>(null);
  useEffect(() => {
    const root = section.current;
    if (!root) return;
    const motion = matchMedia('(prefers-reduced-motion: reduce)');
    const timers = new Set<ReturnType<typeof setInterval>>();
    const scramble = (element: HTMLElement) => {
      const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      const target = element.dataset.scramble!;
      let frame = 0;
      const timer = setInterval(() => {
        frame++;
        element.textContent = target
          .split('')
          .map((letter, index) =>
            frame > index + 3 || !/[A-Z]/.test(letter)
              ? letter
              : letters[(frame * 7 + index * 11) % letters.length],
          )
          .join('');
        if (frame > target.length + 3 || motion.matches) {
          element.textContent = target;
          clearInterval(timer);
          timers.delete(timer);
        }
      }, 45);
      timers.add(timer);
    };
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).dataset.visible = 'true';
            if (
              !motion.matches &&
              (entry.target as HTMLElement).dataset.footerReveal === 'grid'
            )
              entry.target
                .querySelectorAll<HTMLElement>('[data-scramble]')
                .forEach(scramble);
            observer.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.15 },
    );
    root.dataset.animate = 'true';
    root
      .querySelectorAll('[data-footer-reveal]')
      .forEach((element) => observer.observe(element));
    const sync = () => {
      root.dataset.animate = String(!motion.matches);
    };
    sync();
    motion.addEventListener('change', sync);
    return () => {
      observer.disconnect();
      timers.forEach(clearInterval);
      motion.removeEventListener('change', sync);
    };
  }, []);
  return (
    <section
      ref={section}
      className={styles.section}
      id={id}
      data-ocean-footer
      aria-labelledby={`${id}-title`}
    >
      <WebsiteLandscapeVideo
        src="/images/editorial/ocean-editorial.mp4"
        poster="/images/editorial/product-ocean.png"
        className={styles.video}
      />
      <div className={styles.tint} aria-hidden="true" />
      <div className={styles.blurSoft} aria-hidden="true" />
      <div className={styles.blurDeep} aria-hidden="true" />
      <div className={styles.scene}>
        <div className={styles.content}>
          <p className={styles.eyebrow} data-footer-reveal="eyebrow">
            06 / A different kind of ambition
          </p>
          <h2 id={`${id}-title`} data-footer-reveal="heading">
            More room.
            <br />
            <span>More possibility.</span>
          </h2>
          <p className={styles.description} data-footer-reveal="copy">
            Bold horizons. Human scale.
            <br />
            Technology that gives you space.
          </p>
          <div className={styles.actions} data-footer-reveal="action">
            <WebsiteLinkButton href="/auth/sign-up" tone="accent">
              Sign up
            </WebsiteLinkButton>
          </div>
        </div>
      </div>
      <footer className={styles.footer} data-footer-reveal="grid">
        <div className={styles.footerTop} data-footer-reveal="brand">
          <Link href="/" aria-label="Leagen home">
            <Image
              src="/images/brand/leagen-standard-white.svg"
              alt="Leagen"
              width={172}
              height={42}
            />
          </Link>
          <span>Always on. Anytime. Everywhere.</span>
        </div>
        <nav className={styles.navigation} aria-label="Footer">
          <svg
            className={styles.frameLines}
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path d="M0 0 H100 V100" pathLength="1" />
            <path d="M100 100 H0 V0" pathLength="1" />
          </svg>
          <div data-footer-reveal="column">
            <h3>
              <span className="sr-only">01 / Product</span>
              <span aria-hidden="true" data-scramble="01 / PRODUCT">
                01 / PRODUCT
              </span>
            </h3>
            <Link href="/#platform">Platform</Link>
            <Link href="/axcelerate">aXcelerate</Link>
            <Link href="/pricing">Pricing</Link>
          </div>
          <div data-footer-reveal="column">
            <h3>
              <span className="sr-only">02 / Explore</span>
              <span aria-hidden="true" data-scramble="02 / EXPLORE">
                02 / EXPLORE
              </span>
            </h3>
            <Link href="/faq">FAQs</Link>
            <Link href="/blog">Insights</Link>
          </div>
          <div data-footer-reveal="column">
            <h3>
              <span className="sr-only">03 / Connect</span>
              <span aria-hidden="true" data-scramble="03 / CONNECT">
                03 / CONNECT
              </span>
            </h3>
            <Link href="/contact">Contact us</Link>
            <Link href="/auth/sign-in">Sign in</Link>
          </div>
        </nav>
        <div className={styles.footerBottom} data-footer-reveal="legal">
          <span>
            © {new Date().getFullYear()} Rockhawk Pty Ltd trading as Leagen.
            All rights reserved.
          </span>
          <nav aria-label="Legal">
            <Link href="/privacy-policy">Privacy</Link>
            <Link href="/terms-of-service">Terms</Link>
            <Link href="/cookie-policy">Cookies</Link>
            <AnalyticsPreferencesButton />
            <Link href="/dpa">Data processing</Link>
          </nav>
        </div>
      </footer>
    </section>
  );
}
